import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Container,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import axios from 'axios';
import Layout from "../components/layout/CompliceLayout";
import { useCookies } from "react-cookie";
import config from "../config";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const RiskAnalysisDashboard = () => {
  const [timeframe, setTimeframe] = useState('quarterly');
  const [risks, setRisks] = useState([]);
  const { API_BASE_URL } = config;
  const [cookies] = useCookies(["userId"]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/v1/risks/${cookies.userId}`)
      .then(response => {
        // Fix: API returns { success, message, data } structure
        const risksData = response.data?.data || [];
        setRisks(Array.isArray(risksData) ? risksData : []);
      })
      .catch(error => {
        console.error('Error fetching risks:', error);
        setRisks([]); // Set empty array on error
      });
  }, [API_BASE_URL, cookies.userId]);

  // Safe reduce with array check
  const categoryDistribution = Array.isArray(risks) ? risks.reduce((acc, risk) => {
    acc[risk.category] = (acc[risk.category] || 0) + 1;
    return acc;
  }, {}) : {};

  const riskLevels = Array.isArray(risks) ? risks.reduce((acc, risk) => {
    acc[risk.riskLevel] = (acc[risk.riskLevel] || 0) + 1;
    return acc;
  }, {}) : {};

  const riskTrendData = {
    labels: Array.isArray(risks) ? risks.map(risk => new Date(risk.identifiedDate).toLocaleDateString()) : [],
    datasets: [{
      label: 'Risk Score',
      data: Array.isArray(risks) ? risks.map(risk => risk.impactScore * risk.probabilityScore) : [],
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Risk Score Trend'
      }
    }
  };

  const getOverallRiskLevel = () => {
    if (!Array.isArray(risks) || risks.length === 0) return 'No Data';
    const avgRiskScore = risks.reduce((sum, risk) => 
      sum + (risk.impactScore * risk.probabilityScore), 0) / risks.length;
    if (avgRiskScore >= 12) return 'High';
    if (avgRiskScore >= 8) return 'Moderate';
    return 'Low';
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      'High': 'error',
      'Moderate': 'warning',
      'Low': 'success',
      'No Data': 'default'
    };
    return colors[level] || 'default';
  };

  const overallRiskLevel = getOverallRiskLevel();

  // Safe status distribution calculation
  const statusDistribution = Array.isArray(risks) ? risks.reduce((acc, risk) => {
    acc[risk.currentStatus] = (acc[risk.currentStatus] || 0) + 1;
    return acc;
  }, {}) : {};

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1">Risk Analysis Dashboard</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            
            <Chip
              label={`Overall Risk Level: ${overallRiskLevel}`}
              color={getRiskLevelColor(overallRiskLevel)}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Risk Category Distribution</Typography>
                {Object.keys(categoryDistribution).length > 0 ? (
                  Object.entries(categoryDistribution).map(([category, count]) => (
                    <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{category}</Typography>
                      <Chip label={count} variant="outlined" />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No risk categories available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Key Risk Indicators</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>Risk Levels</Typography>
                    {Object.keys(riskLevels).length > 0 ? (
                      Object.entries(riskLevels).map(([level, count]) => (
                        <Box key={level} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>{level}</Typography>
                          <Chip label={count} variant="outlined" />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No risk levels available
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>Latest Risks</Typography>
                    {Array.isArray(risks) && risks.length > 0 ? (
                      risks.slice(0, 3).map(risk => (
                        <Box key={risk._id || risk.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography noWrap sx={{ maxWidth: '70%' }}>{risk.title}</Typography>
                          <Chip
                            label={risk.riskLevel}
                            color={getRiskLevelColor(risk.riskLevel)}
                            size="small"
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No risks available
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>Status Distribution</Typography>
                    {Object.keys(statusDistribution).length > 0 ? (
                      Object.entries(statusDistribution).map(([status, count]) => (
                        <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>{status}</Typography>
                          <Chip label={count} variant="outlined" />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No status data available
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default RiskAnalysisDashboard;
